import store from '../../../../store';
import { PRODUCTIONS_ACTIONS } from '../../../../store/v1/productions/productions.constants';
import get from 'lodash/get';
import type { ResourcingScheduledAction } from 'v5/features/productions/resourcing/model/actions';
import type { SystemEffect } from 'v5/services/internal/MessageService/_private/SystemEffectMessage';
import type { MessageService } from 'v5/services/internal/MessageService/MessageService';

const updatePayload = { type: 'update' as const };

export type ProductionResourcingAffectedEntities = {
  productionId: number;
  bookingIds?: readonly number[];
};

/**
 * System effects to refresh production resourcing after a mutation.
 * Include every entity whose caches must invalidate (production, bookings, etc.).
 */
export function getSystemEffectsForProductionResourcingUpdate(
  entities: ProductionResourcingAffectedEntities
): SystemEffect[] {
  const scope: SystemEffect[] = [
    {
      entity: 'production',
      entityId: entities.productionId,
      payload: updatePayload
    }
  ];

  for (const bookingId of entities.bookingIds ?? []) {
    if (!Number.isFinite(bookingId)) {
      continue;
    }
    scope.push({
      entity: 'booking',
      entityId: bookingId,
      payload: updatePayload
    });
  }

  return scope;
}

export function getSystemEffectsForBookingUpdate(
  bookingId: number
): SystemEffect[] {
  return [
    {
      entity: 'booking',
      entityId: bookingId,
      payload: updatePayload
    }
  ];
}

export function getSystemEffectsForExpenseUpdate(
  expenseId: number
): SystemEffect[] {
  return [
    {
      entity: 'expense',
      entityId: expenseId,
      payload: updatePayload
    }
  ];
}

const bookingScopedResourcingActionTypes = new Set<
  ResourcingScheduledAction['type']
>([
  'UPDATE_BOOKING_RATE',
  'UPDATE_BOOKING_FEES',
  'UPDATE_BOOKING_RATES_AND_FEES',
  'UPDATE_BOOKING_EXPENSE_LINES',
  'UPDATE_BOOKING_STATUS'
]);

export function getSystemEffectsForResourcingScheduledAction(
  productionId: number,
  action: ResourcingScheduledAction,
  resolveId: (id: number) => number
): SystemEffect[] {
  let bookingIds: number[] | undefined;

  if (bookingScopedResourcingActionTypes.has(action.type)) {
    const bookingPayload = action.payload as { bookingId?: number };
    const rawBookingId =
      typeof bookingPayload.bookingId === 'number'
        ? bookingPayload.bookingId
        : null;
    if (rawBookingId != null) {
      bookingIds = [resolveId(rawBookingId)];
    }
  }

  return getSystemEffectsForProductionResourcingUpdate({
    productionId,
    bookingIds
  });
}

function dispatchV1ProductionUpdate(productionId: number) {
  const state = store.getState() as Record<string, unknown>;
  const production = get(state, ['productions', 'data', productionId]);
  if (!production) {
    return;
  }
  store.dispatch({
    type: PRODUCTIONS_ACTIONS.UPDATE_PRODUCTION_SUCCESS,
    production_id: productionId,
    result: production
  });
}

export async function awaitReportSystemEffects(
  messageService: MessageService,
  scope: SystemEffect[],
  options?: { timeoutMs?: number }
): Promise<void> {
  if (scope.length === 0) {
    return;
  }

  await messageService.reportSystemEffect.sendRequest(
    { scope },
    options?.timeoutMs ?? 3000
  );

  for (const effect of scope) {
    if (effect.entity === 'production') {
      dispatchV1ProductionUpdate(effect.entityId);
    }
  }
}
